<template>
  <div 
    class="relative rounded-lg p-3"
    :class="{
      'space-y-4': true,
      'border-2 border-orange-400': isInherited
    }"
  >
    <!-- Floating Inheritance Label (absolute positioned, doesn't affect layout) -->
    <div 
      v-if="isInherited" 
      class="absolute -top-2.5 left-3 flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-background"
    >
      <GitBranch class="h-3 w-3 text-orange-500" />
      <span class="text-xs text-orange-500 font-medium">
        From {{ inheritedFromSceneName }}
      </span>
    </div>
    
    <!-- Effect Selector -->
    <div class="space-y-2">
      <div class="flex items-center justify-between">
        <Label class="text-sm font-medium">Effect</Label>
        <div class="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            @click="handleSetSolidMode"
            class="h-6 px-2 text-xs"
            title="Use solid color mode (single pip)"
          >
            Solid
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-6 w-6"
            @click="handleClearEffect"
            title="Reset to inherited settings"
          >
            <RotateCcw class="h-3 w-3" />
          </Button>
        </div>
      </div>
      <Popover v-model:open="effectPopoverOpen">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            class="w-full justify-between"
          >
            <span class="flex items-center gap-2">
              <Sparkles class="h-4 w-4" />
              {{ effectName }}
            </span>
            <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search effects..." />
            <CommandEmpty>No effect found.</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="effect in availableEffects"
                  :key="effect.id"
                  :value="effect.name"
                  @select="selectEffect(effect)"
                >
                  <Check
                    :class="[
                      'mr-2 h-4 w-4',
                      effectId === effect.id ? 'opacity-100' : 'opacity-0'
                    ]"
                  />
                  <span>{{ effect.name }}</span>
                  <span class="ml-auto text-xs text-muted-foreground">#{{ effect.id }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>

    <!-- Speed & Intensity Sliders - side by side (hidden in Solid mode) -->
    <div v-if="effectId !== 0" class="grid grid-cols-2 gap-4">
      <!-- Speed Slider -->
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <Label class="text-sm font-medium">Speed</Label>
          <span class="text-xs text-muted-foreground">{{ speed }}</span>
        </div>
        <Slider
          :model-value="[speed]"
          @update:model-value="(v) => v && setSpeed(v[0])"
          :min="0"
          :max="255"
          :step="1"
          class="w-full"
        />
      </div>

      <!-- Intensity Slider -->
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <Label class="text-sm font-medium">Intensity</Label>
          <span class="text-xs text-muted-foreground">{{ intensity }}</span>
        </div>
        <Slider
          :model-value="[intensity]"
          @update:model-value="(v) => v && setIntensity(v[0])"
          :min="0"
          :max="255"
          :step="1"
          class="w-full"
        />
      </div>
    </div>

    <!-- Palette Selector (optional, hidden in Solid mode) -->
    <div v-if="effectId !== 0" class="space-y-2">
      <div class="flex items-center justify-between">
        <Label class="text-sm font-medium">Palette</Label>
        <Button
          v-if="paletteId !== undefined"
          variant="ghost"
          size="sm"
          @click="clearPalette"
          class="h-6 px-2 text-xs"
        >
          Clear
        </Button>
      </div>
      <Popover v-model:open="palettePopoverOpen">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            class="w-full justify-between"
          >
            <span class="flex items-center gap-2">
              <Palette class="h-4 w-4" />
              {{ paletteName }}
            </span>
            <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search palettes..." />
            <CommandEmpty>No palette found.</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="pal in availablePalettes"
                  :key="pal.id"
                  :value="pal.name"
                  @select="selectPalette(pal)"
                >
                  <Check
                    :class="[
                      'mr-2 h-4 w-4',
                      paletteId === pal.id ? 'opacity-100' : 'opacity-0'
                    ]"
                  />
                  <span>{{ pal.name }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>

    <!-- Gradient Editor (hidden in Solid mode) -->
    <div v-if="effectId !== 0" class="space-y-2">
      <div class="flex items-center justify-between">
        <Label class="text-sm font-medium">Colors</Label>
        <Button
          variant="ghost"
          size="sm"
          @click="resetGradient"
          class="h-6 px-2 text-xs"
          title="Reset to red-blue gradient"
        >
          Reset
        </Button>
      </div>
      <GradientEditor
        :gradient="gradient"
        @update:stop-position="updateStopPosition"
        @update:stop-color="updateStopColor"
        @add-stop="addGradientStop"
        @remove-stop="removeGradientStop"
      />
    </div>
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Check, ChevronsUpDown, Sparkles, Palette, RotateCcw, GitBranch } from 'lucide-vue-next'
import GradientEditor from './GradientEditor.vue'
import { useWledEffects } from '@/composables/useWledEffects'
import type { WledEffect, WledPalette } from '@/types/wledEffect'

const props = defineProps<{
  lightId: string
  sceneId: string
}>()

const {
  effectId,
  effectName,
  speed,
  intensity,
  paletteId,
  gradient,
  availableEffects,
  availablePalettes,
  isInherited,
  inheritedFromSceneName,
  setEffect,
  setSpeed,
  setIntensity,
  setPalette,
  updateStopPosition,
  updateStopColor,
  addGradientStop,
  removeGradientStop,
  resetGradient,
  setSolidColor,
  clearEffectSettings,
  initialize
} = useWledEffects(props.lightId, props.sceneId)

const emit = defineEmits<{
  clearEffect: []
}>()

// Popover states
const effectPopoverOpen = ref(false)
const palettePopoverOpen = ref(false)

// Computed palette name
const paletteName = computed(() => {
  if (paletteId.value === undefined) return 'Custom Colors'
  return availablePalettes.value.find(p => p.id === paletteId.value)?.name || `Palette ${paletteId.value}`
})

// Select effect
function selectEffect(effect: WledEffect) {
  setEffect(effect.id, effect.name)
  effectPopoverOpen.value = false
}

// Select palette
function selectPalette(pal: WledPalette) {
  setPalette(pal.id)
  palettePopoverOpen.value = false
}

// Clear palette
function clearPalette() {
  setPalette(undefined)
}

// Handle clear effect - clears settings and emits event
async function handleClearEffect() {
  await clearEffectSettings()
  emit('clearEffect')
}

// Handle switching to solid mode
function handleSetSolidMode() {
  setEffect(0, 'Solid')
  setSolidColor()
}

// Initialize on mount
onMounted(() => {
  initialize()
})
</script>
